import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Info, Lock, Truck } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import RazorpayMock from "@/components/RazorpayMock";
import { openRazorpay } from "@/lib/razorpay";
import AddressFields, { EMPTY_ADDRESS } from "@/components/AddressFields";
import GiftWrapToggle from "@/components/GiftWrapToggle";
import PromoCode, { useCredit } from "@/components/PromoCode";
import { api, errMsg, inr, SIZES } from "@/lib/api";

export default function Checkout() {
  const { bookId } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const reorderId = sp.get("reorder");
  const [book, setBook] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [addr, setAddr] = useState(EMPTY_ADDRESS);
  const [rzp, setRzp] = useState(null);
  const [promo, setPromo] = useState(null);
  const [wrap, setWrap] = useState(false);
  const [giftMsg, setGiftMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/books/${bookId}`).then((r) => setBook(r.data)).catch((e) => { toast.error(errMsg(e)); nav("/dashboard"); });
    api.get("/config").then((r) => setCfg(r.data));
  }, [bookId, nav]);
  useEffect(() => {
    if (!reorderId) return;
    api.get(`/orders/${reorderId}`).then((r) => {
      const o = r.data;
      if (o.address) setAddr({ ...EMPTY_ADDRESS, ...o.address });
      setWrap(!!o.gift_wrap); setGiftMsg(o.gift_message || "");
      toast.success("Details copied from your previous order — just review and pay.");
    }).catch(() => {});
  }, [reorderId]);
  const credit = useCredit(promo ? promo.total : book?.pricing.total || 0);
  if (!book) return null;
  const pr = book.pricing;
  const total = (promo ? promo.total : pr.total) - credit;
  const primary = cfg?.providers.find((p) => p.key === cfg.primary_provider);

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!/^[1-9][0-9]{5}$/.test(addr.pincode)) return toast.error("Please enter a valid 6-digit PIN code");
    setBusy(true);
    try {
      const qs = new URLSearchParams({ ...(promo ? { promo: promo.code } : {}), ...(wrap ? { gift_wrap: "true" } : {}), ...(wrap && giftMsg.trim() ? { gift_message: giftMsg.trim() } : {}) }).toString();
      const { data } = await api.post(`/books/${bookId}/orders${qs ? `?${qs}` : ""}`, addr);
      if (data.razorpay.mocked) setRzp({ order: data.order, amount: data.razorpay.amount / 100 });
      else {
        setBusy(true);
        await openRazorpay(data.razorpay, {
          onSuccess: (res) => verify(data.order.id, res),
          onDismiss: () => { setBusy(false); toast("Payment not completed — your order is saved, you can pay again anytime."); },
        });
        return;
      }
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const verify = async (orderId, payment) => {
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/verify`, payment);
      toast.success("Payment received — your book is on its way to the printer");
      nav(`/orders/${orderId}`);
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  const pay = async () => {
    const { data: payment } = await api.post(`/orders/${rzp.order.id}/mock-pay`).catch((err) => { toast.error(errMsg(err)); return { data: null }; });
    if (payment) await verify(rzp.order.id, payment);
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="mx-auto grid max-w-6xl gap-10 px-5 py-12 sm:px-8 lg:grid-cols-[1fr_380px]">
        <form onSubmit={placeOrder} data-testid="checkout-address-form">
          <p className="overline">Checkout</p>
          <h1 className="mt-2 font-serif text-4xl tracking-tight">Where should we send it?</h1>
          <div className="mt-8"><AddressFields addr={addr} setAddr={setAddr} /></div>
          <GiftWrapToggle checked={wrap} onChange={setWrap} message={giftMsg} onMessage={setGiftMsg} />
          <div className="mt-8 flex gap-3 rounded-lg border border-[#E8E2D9] bg-[#F5F0E8]/60 p-4 text-sm text-[#5E5868]" data-testid="checkout-delivery-note">
            <Truck className="mt-0.5 h-5 w-5 shrink-0 text-[#3B5BDB]" />
            <p>
              <strong className="text-[#26232E]">Estimated delivery: about 2 weeks.</strong> Printed by {primary?.name || "our India-local partner"}. Two weeks is our target, not a hard guarantee — we'll update you at every step.
              {primary && !primary.domestic && " This order may print outside India, so delivery could take longer."}
            </p>
          </div>
          <button type="submit" disabled={busy} data-testid="checkout-proceed-razorpay-btn" className="btn-primary mt-8 w-full sm:w-auto">
            <Lock className="h-4 w-4" />Pay {inr(total)} with Razorpay
          </button>
        </form>
        <aside className="paper h-fit rounded-lg p-6 lg:sticky lg:top-24" data-testid="checkout-summary">
          <h2 className="font-serif text-2xl">{book.title}</h2>
          <p className="text-sm text-[#8C8498]">{SIZES.find((s) => s.key === book.size)?.label} {book.size} · {book.page_count} pages</p>
          <div className="mt-5"><PromoCode amount={pr.total} promo={promo} onChange={setPromo} /></div>
          <dl className="mt-6 space-y-2.5 text-sm">
            <div className="flex justify-between"><dt>Book (incl. 20 pages)</dt><dd>{inr(pr.base)}</dd></div>
            <div className="flex justify-between"><dt>Extra pages ({pr.extra_pages} × ₹25)</dt><dd>{inr(pr.extra)}</dd></div>
            <div className="flex justify-between"><dt>AI light enhancement</dt><dd className="text-[#789A82]">Free</dd></div>
            <div className="flex justify-between"><dt>Shipping</dt><dd>{pr.shipping ? inr(pr.shipping) : <span className="text-[#789A82]">Free</span>}</dd></div>
            {promo && <div className="flex justify-between text-[#4F7A5C]"><dt>Promo {promo.code}</dt><dd data-testid="checkout-discount">−{inr(promo.discount)}</dd></div>}
            {credit > 0 && <div className="flex justify-between text-[#3B5BDB]"><dt>Referral credit</dt><dd data-testid="checkout-credit">−{inr(credit)}</dd></div>}
            {wrap && <div className="flex justify-between"><dt>Gift wrap</dt><dd className="text-[#789A82]" data-testid="checkout-giftwrap-line">Free</dd></div>}
            <div className="flex justify-between border-t border-[#E8E2D9] pt-3 text-base font-semibold"><dt>Total</dt><dd data-testid="checkout-total">{inr(total)}</dd></div>
          </dl>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-[#8C8498]"><Info className="h-3.5 w-3.5" />GST included · free shipping above ₹1,999</p>
        </aside>
      </main>
      <RazorpayMock open={!!rzp} amount={rzp?.amount} busy={busy} onPay={pay} onClose={() => setRzp(null)} />
    </div>
  );
}
